import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Users, Phone, Mail } from 'lucide-react'
import { Badge } from '@/components/ui'
import type { Customer } from '@/types'

export interface CustomerCardProps {
  customer: Customer
}

/**
 * Customer list card (EP-109, relaid 2026-09-29). Avatar on the left; beside it
 * the name, then one row of chips (status, unpaid, LINE), then contact rows. The whole card is a
 * link to the customer detail page. Extracted as a dedicated component so the
 * grid item stays independent of the list page's filter/chrome logic.
 */
export function CustomerCard({ customer }: CustomerCardProps) {
  const { t } = useTranslation('customers')

  // Status badge — matches the pre-EP-109 getStatusBadge exactly: the four
  // known statuses get a translated label + variant; any other value renders
  // the raw status string with the default badge (no variant), never a
  // missing-translation lookup.
  const statusBadge = (() => {
    switch (customer.status) {
      case 'active':
        return <Badge variant="success">{t('status.active')}</Badge>
      case 'alumni':
        return <Badge variant="ghost">{t('status.alumni')}</Badge>
      case 'prospect':
        return <Badge variant="warning">{t('status.prospect')}</Badge>
      case 'external':
        return <Badge variant="info">{t('status.external')}</Badge>
      default:
        return <Badge>{customer.status}</Badge>
    }
  })()

  return (
    <Link
      to={`/customers/${customer.id}`}
      className="flex items-start gap-3 p-4 bg-base-100 border border-base-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
    >
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
        <Users className="w-5 h-5 text-primary" />
      </div>
      {/* One column beside the avatar: the name gets the full width, every
          chip sits on one row under it, and the contact rows line up with
          both (2026-09-29: chips beside the name wrapped unevenly on a phone). */}
      <div className="min-w-0 flex-1 space-y-2">
        <h3 className="font-semibold leading-snug break-words">{customer.name}</h3>
        <div className="flex flex-wrap items-center gap-1.5">
          {statusBadge}
          {/* P008: unpaid chip — shown when the customer has any unpaid invoice */}
          {customer.has_unpaid_invoices && (
            <Badge variant="error" data-testid="customer-unpaid-chip">
              {t('chips.unpaid')}
            </Badge>
          )}
          {customer.source === 'line' && (
            <Badge variant="success" outline>
              {t('line_badge')}
            </Badge>
          )}
        </div>
        {(customer.phone || customer.email) && (
          <div className="space-y-1 text-sm text-base-content/60">
            {customer.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-3 h-3 shrink-0" />
                <span>{customer.phone}</span>
              </div>
            )}
            {customer.email && (
              <div className="flex items-center gap-2 min-w-0">
                <Mail className="w-3 h-3 shrink-0" />
                <span className="truncate">{customer.email}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Link>
  )
}
